import { useState, useEffect, useCallback } from 'react';
import { fetchWithAuth } from './authHelpers';

export const useAchievements = (userId = null, season = null) => {
    const [achievements, setAchievements] = useState([]);
    const [allAchievements, setAllAchievements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [checkingAchievements, setCheckingAchievements] = useState(false);
    const [newAchievements, setNewAchievements] = useState([]);

    // Fetch all available achievements
    const fetchAllAchievements = useCallback(async () => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/achievements`, 'GET');
            if (!response.ok) {
                throw new Error('Failed to fetch achievements');
            }
            const data = await response.json();
            setAllAchievements(data);
            return data;
        } catch (error) {
            console.error('Error fetching all achievements');
            setError(error.message);
            return [];
        }
    }, []);

    // Fetch user's achievements
    const fetchUserAchievements = useCallback(async (targetUserId = null, targetSeason = null) => {
        if (!targetUserId && !userId) return;

        setLoading(true);
        setError(null);
        try {
            const userIdToUse = targetUserId || userId;
            let url = `${process.env.REACT_APP_BACKEND_URL}/achievements/user/${userIdToUse}`;
            
            // Add season query parameter if provided
            if (targetSeason || season) {
                url += `?season=${targetSeason || season}`;
            }

            const response = await fetchWithAuth(url, 'GET');
            if (!response.ok) {
                throw new Error('Failed to fetch user achievements');
            }
            const data = await response.json();
            setAchievements(data);
            return data;
        } catch (error) {
            console.error('Error fetching user achievements');
            setError(error.message);
            return [];
        } finally {
            setLoading(false);
        }
    }, [userId, season]);

    // Check and unlock achievements for a user
    const checkAchievements = useCallback(async (targetUserId = null, targetSeason = null) => {
        if (!targetUserId && !userId) return;

        setCheckingAchievements(true);
        setError(null);
        try {
            const userIdToUse = targetUserId || userId;
            const requestBody = {};
            
            // Add season to request body if provided
            if (targetSeason || season) {
                requestBody.season = targetSeason || season;
            }

            const response = await fetchWithAuth(
                `${process.env.REACT_APP_BACKEND_URL}/achievements/check/${userIdToUse}`,
                'POST',
                requestBody
            );
            
            if (!response.ok) {
                throw new Error('Failed to check achievements');
            }
            
            const data = await response.json();
            setNewAchievements(data.achievements || []);
            
            // Refresh user achievements after checking
            await fetchUserAchievements(targetUserId, targetSeason);
            
            return data;
        } catch (error) {
            console.error('Error checking achievements');
            setError(error.message);
            return null;
        } finally {
            setCheckingAchievements(false);
        }
    }, [userId, season, fetchUserAchievements]);

    // Get achievement statistics
    const getAchievementStats = useCallback((userAchievements = achievements) => {
        // Ensure userAchievements is an array
        const achievementsArray = Array.isArray(userAchievements) ? userAchievements : [];
        
        const total = achievementsArray.length;
        const completed = achievementsArray.filter(a => a.isCompleted).length;
        const inProgress = achievementsArray.filter(a => !a.isCompleted && a.progress.percentage > 0).length;
        const notStarted = achievementsArray.filter(a => !a.isCompleted && a.progress.percentage === 0).length;
        
        return {
            total,
            completed,
            inProgress,
            notStarted,
            completionRate: total > 0 ? (completed / total) * 100 : 0
        };
    }, [achievements]);

    // Get achievements by category
    const getAchievementsByCategory = useCallback((userAchievements = achievements) => {
        const categorized = {};
        userAchievements.forEach(userAchievement => {
            const category = userAchievement.achievement?.category || 'unknown';
            if (!categorized[category]) {
                categorized[category] = [];
            }
            categorized[category].push(userAchievement);
        });
        return categorized;
    }, [achievements]);

    // Get achievements by rarity
    const getAchievementsByRarity = useCallback((userAchievements = achievements) => {
        const byRarity = {};
        userAchievements.forEach(userAchievement => {
            const rarity = userAchievement.achievement?.rarity || 'common';
            if (!byRarity[rarity]) {
                byRarity[rarity] = [];
            }
            byRarity[rarity].push(userAchievement);
        });
        return byRarity;
    }, [achievements]);

    // Get recently completed achievements
    const getRecentlyCompleted = useCallback((userAchievements = achievements, days = 7) => {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - days);
        
        return userAchievements.filter(userAchievement => {
            if (!userAchievement.isCompleted || !userAchievement.unlockedAt) return false;
            return new Date(userAchievement.unlockedAt) > cutoffDate;
        });
    }, [achievements]);

    // Clear new achievements notification
    const clearNewAchievements = useCallback(() => {
        setNewAchievements([]);
    }, []);

    // Initial fetch when component mounts or userId/season changes
    useEffect(() => {
        if (userId) {
            fetchUserAchievements();
        }
    }, [userId, season, fetchUserAchievements]);

    // Fetch all achievements on mount
    useEffect(() => {
        fetchAllAchievements();
    }, [fetchAllAchievements]);

    return {
        // Data
        achievements,
        allAchievements,
        newAchievements,
        
        // Loading states
        loading,
        checkingAchievements,
        
        // Error state
        error,
        
        // Functions
        fetchUserAchievements,
        fetchAllAchievements,
        checkAchievements,
        clearNewAchievements,
        
        // Utility functions
        getAchievementStats,
        getAchievementsByCategory,
        getAchievementsByRarity,
        getRecentlyCompleted,
        
        // Computed values
        stats: getAchievementStats(),
        categorized: getAchievementsByCategory(),
        byRarity: getAchievementsByRarity(),
        recentlyCompleted: getRecentlyCompleted()
    };
};

// Hook specifically for checking achievements (useful for triggering after actions)
export const useAchievementChecker = () => {
    const [checking, setChecking] = useState(false);
    const [error, setError] = useState(null);

    const checkUserAchievements = useCallback(async (userId, season = null) => {
        if (!userId) return null;

        setChecking(true);
        setError(null);
        try {
            const requestBody = season ? { season } : {};
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_BACKEND_URL}/achievements/check/${userId}`,
                'POST',
                requestBody
            );
            
            if (!response.ok) {
                throw new Error('Failed to check achievements');
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error checking achievements');
            setError(error.message);
            return null;
        } finally {
            setChecking(false);
        }
    }, []);

    return {
        checkUserAchievements,
        checking,
        error
    };
};

export default useAchievements;
